import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { compose, setDisplayName, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { CircleMenuItem } from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductCirclesMenu';
import MultiCourseProductCirclesMenu from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductCirclesMenu';
import MultiCourseProductCourseCards from 'bundles/enterprise-legacy-learner-home/components/multiCourseProductCard/MultiCourseProductCourseCards';
import { PRODUCT_ID_INFIX } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import { useSkillProgressDeepLink } from 'bundles/enterprise-legacy-learner-home/hooks/useSkillProgressDeepLink';
import Naptime from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';
import SkillsDialog from 'bundles/skills-common/shared/verified-skills/components/SkillsDialog';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type S12nCourseState = {
  isEnrolled: boolean;
  isCompleted: boolean;
  courseId: string;
};

type PropsFromNaptime = {
  curriculumProduct?: {
    s12nCourseStates: S12nCourseState[];
  };
};

type PropsFromWithProps = {
  circleMenuData: CircleMenuItem[];
  initialSelectedCourseId: string;
};

type PropsFromCaller = {
  userId: number;
  itemType: 'COURSE' | 'S12N' | 'BADGE';
  id: string;
  programId: string;
  collectionId?: string;
  name: string;
  circleMenuData?: CircleMenuItem[];
  courseCount: number;
  capstoneCourses?: string[];
  firstCourseId: string;
  lastActiveCourseId?: string;
};

type Props = PropsFromCaller & PropsFromWithProps;

const MultiCourseProductEnrolledCardExtended = ({
  userId,
  programId,
  collectionId,
  name,
  courseCount,
  id,
  circleMenuData,
  capstoneCourses,
  initialSelectedCourseId,
  lastActiveCourseId,
}: Props) => {
  const [currentCourseId, setCurrentCourseId] = useState(initialSelectedCourseId);
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false);
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const isSkillPathway = badgeMetadata?.isSkillPathway;
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const track = useTracker();

  const handleCirclesMenuSelect = (selectedCourseId: string) => {
    setCurrentCourseId(selectedCourseId);
  };

  const openSkillsDialog = useCallback<() => void>(() => setSkillsDialogOpen(true), []);

  // Handle deep link from email - auto-open dialog if query param matches this badge
  const { clearDeepLinkParam } = useSkillProgressDeepLink({
    isSkillPathway,
    badgeTemplateId: badgeMetadata?.badge?.badgeTemplateId,
    onDeepLinkMatch: openSkillsDialog,
  });

  const handleSkillsDialogClose = useCallback<() => void>(() => {
    clearDeepLinkParam();
    setSkillsDialogOpen(false);
  }, [clearDeepLinkParam]);

  React.useEffect(() => {
    if (lastActiveCourseId) {
      // if there is a last active course Id, set as current after it as loaded
      setCurrentCourseId(lastActiveCourseId);
    }
  }, [lastActiveCourseId]);

  const currentCircleMenuData = useMemo(
    () =>
      circleMenuData.map((circleMenuItem) => ({
        ...circleMenuItem,
        isActive: circleMenuItem.courseId === currentCourseId,
      })),
    [circleMenuData, currentCourseId]
  );

  // Create custom message and action button for skill pathways
  const customCourseCountMessage = isSkillPathway ? (
    <FormattedMessage
      message={_t('{courseCount, plural, =1 {# skill goal} other {# skill goals}}')}
      courseCount={courseCount}
    />
  ) : undefined;

  const circleMenuActionButton = isSkillPathway ? (
    <React.Fragment>
      <Button
        variant="secondary"
        size="small"
        onClick={() => {
          track('click_button', {
            button: {
              name: 'skill_pathway_view_skills_progress',
            },
            enterprise: {
              ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
              ...(program?.slug ? { programSlug: program?.slug } : {}),
            },
            product: {
              id: badgeMetadata?.badge?.badgeTemplateId ?? '',
              type: 'learning_path',
            },
          });
          setSkillsDialogOpen(true);
        }}
      >
        {_t('View skill progress')}
      </Button>
      <SkillsDialog open={skillsDialogOpen} onClose={handleSkillsDialogClose} courseId={currentCourseId} />
    </React.Fragment>
  ) : undefined;

  if (circleMenuData.length === 0) return null;

  return (
    <div
      className="rc-MultiCourseProductEnrolledCardExtended"
      data-e2e={`MultiCourseProductEnrolledCardExtended~${id}`}
    >
      <MultiCourseProductCirclesMenu
        circleMenuData={currentCircleMenuData}
        handleSelect={handleCirclesMenuSelect}
        courseCount={courseCount}
        name={name}
        capstoneProjectCount={capstoneCourses?.length}
        actionButton={circleMenuActionButton}
        courseCountMessage={customCourseCountMessage}
      />
      <MultiCourseProductCourseCards
        circleMenuData={currentCircleMenuData}
        userId={userId}
        programId={programId}
        collectionId={collectionId}
      />
    </div>
  );
};

export default compose<Props, PropsFromCaller>(
  setDisplayName('S12nEnrolledCardExtendedHOC'),
  Naptime.createContainer<PropsFromNaptime, PropsFromCaller>(({ circleMenuData, programId, id }) => ({
    curriculumProduct: !circleMenuData
      ? ProgramCurriculumProductsV1.get(tupleToStringKey([programId, PRODUCT_ID_INFIX.S12N, id]), {})
      : undefined,
  })),
  // TODO(brivera): Remove this and the call to curriculumProduct when we fully rollout the new enrolled products query
  withProps<PropsFromWithProps, PropsFromNaptime & PropsFromCaller>(
    ({ curriculumProduct, firstCourseId, circleMenuData, capstoneCourses, lastActiveCourseId }) => {
      const initialSelectedCourseId = lastActiveCourseId ?? firstCourseId;

      if (circleMenuData) {
        return {
          circleMenuData: circleMenuData.map((circleMenuItem) => ({
            ...circleMenuItem,
            isActive: circleMenuItem.courseId === initialSelectedCourseId,
          })),
          initialSelectedCourseId,
        };
      }

      const s12nCourseStates = curriculumProduct?.s12nCourseStates;

      // We need to do this so that the s12n card still renders correctly in control
      const defaultCircleMenuData =
        s12nCourseStates?.map(({ isEnrolled, isCompleted, courseId }, index) => ({
          courseId,
          isActive: initialSelectedCourseId === courseId,
          isEnrolled,
          isCompleted,
          index,
          isCapstone: capstoneCourses?.includes(courseId),
        })) ?? [];

      return {
        circleMenuData: defaultCircleMenuData,
        initialSelectedCourseId,
      };
    }
  )
)(MultiCourseProductEnrolledCardExtended);

export const BaseComponent = MultiCourseProductEnrolledCardExtended;
